import {Component, forwardRef, Input, OnInit} from '@angular/core';
import {AbstractControl, ControlValueAccessor, NG_VALUE_ACCESSOR, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {SearchproductService} from '@services/searchproduct.service';
import {AvailableParamsResource} from '@services/resource/available-params-resource';
import {MarkantElementsModule} from '@markant/elements-angular';
import {CommonModule} from '@angular/common';

export interface ProductGroup {
  value: string;
  viewValue: string;
}

export interface ProductCategory {
  disabled?: boolean;
  name: string;
  productGroups: ProductGroup[];
}

export const productCategories: ProductCategory[] = [
  {
    name: 'Food + Drogerie',
    productGroups: [
      {value: 'ALKOHOLFREIE-GETRAENKE', viewValue: 'Alkoholfreie Getränke'},
      {value: 'BABYNAHRUNG', viewValue: 'Babynahrung'},
      {value: 'BABYPFLEGE', viewValue: 'Babypflege'},
      {value: 'BIER', viewValue: 'Bier'},
      {value: 'CEREALIEN', viewValue: 'Cerealien'},
      {value: 'DAUERBACKWAREN', viewValue: 'Dauerbackwaren'},
      {value: 'DEKORATIVE-KOSMETIK', viewValue: 'Dekorative Kosmetik'},
      {value: 'DUFT', viewValue: 'Duft'},
      {value: 'FEINKOST-GEKUEHLT', viewValue: 'Feinkost gekühlt'},
      {value: 'FEINKOST-UNGEKUEHLT', viewValue: 'Feinkost ungekühlt'},
      {value: 'FERTIGGERICHTE-GEKUEHLT', viewValue: 'Fertiggerichte gekühlt'},
      {value: 'FERTIGGERICHTE-UNGEKUEHLT', viewValue: 'Fertiggerichte ungekühlt'},
      {value: 'FLEISCH', viewValue: 'Fleisch (frisch und gefroren)'},
      {value: 'GROSSVERBRAUCHER', viewValue: 'Großverbraucher'},
      {value: 'GRUNDNAHRUNG', viewValue: 'Grundnahrung'},
      {value: 'HEISSGETRAENKE', viewValue: 'Heißgetränke'},
      {value: 'HYGIENEPAPIER', viewValue: 'Hygienepapier'},
      {value: 'KAESE', viewValue: 'Käse'},
      {value: 'KONSERVEN', viewValue: 'Konserven'},
      {value: 'KOERPERPFLEGE', viewValue: 'Körperpflege'},
      {value: 'MARGARINE-FETTE-OELE', viewValue: 'Margarine / Fette / Öle'},
      {value: 'MOLKEREIPRODUKTE', viewValue: 'Molkereiprodukte'},
      {value: 'NEARFOOD', viewValue: 'Nearfood'},
      {value: 'OTC', viewValue: 'OTC'},
      {value: 'SALZIGE-SNACKS', viewValue: 'Salzige Snacks'},
      {value: 'SCHOKOLADE', viewValue: 'Schokolade'},
      {value: 'SEKT', viewValue: 'Sekt'},
      {value: 'SPIRITUOSEN', viewValue: 'Spirituosen'},
      {value: 'SUESSE-BROTAUFSTRICHE', viewValue: 'Süße Brotaufstriche'},
      {value: 'TABAK', viewValue: 'Tabak'},
      {value: 'TIEFKUEHLKOST', viewValue: 'Tiefkühlkost'},
      {value: 'TIERNAHRUNG', viewValue: 'Tiernahrung'},
      {value: 'TROCKENFRUECHTE', viewValue: 'Trockenfrüchte'},
      {value: 'WASCH-PUTZ-REINIGUNGSMITTEL', viewValue: 'Wasch-/Putz-/Reinigungsmittel'},
      {value: 'WEIN', viewValue: 'Wein'},
      {value: 'WURSTWAREN', viewValue: 'Wurstwaren'},
      {value: 'ZUCKERWAREN', viewValue: 'Zuckerwaren'},
    ],
  },
  {
    name: 'Non-Food',
    productGroups: [
      {value: 'NONFOOD', viewValue: 'Nonfood'},
      {value: 'SONSTIGES', viewValue: 'Sonstiges'},
    ],
  },
  {
    name: 'Technik',
    productGroups: [
      {value: 'DIENSTLEISTUNGEN', viewValue: 'Dienstleistungen'},
      {value: 'SOFTWARE', viewValue: 'Software'},
      {value: 'TECHNIK', viewValue: 'Technik'},
    ],
  },
];

@Component({
  selector: 'app-product-group-select',
  templateUrl: './product-group-select.component.html',
  styleUrls: ['./product-group-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ProductGroupSelectComponent),
      multi: true,
    },
  ],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MarkantElementsModule],
})
export class ProductGroupSelectComponent implements ControlValueAccessor, OnInit {
  @Input() allowEmpty = false;
  @Input() availableOnly = false;

  public productGroupForm: UntypedFormGroup = new UntypedFormGroup({
    selectedProductGroup: new UntypedFormControl('all', this.allowEmpty ? [] : [Validators.required]),
  });

  public productCategories: ProductCategory[] = productCategories;

  constructor(private readonly searchProductService: SearchproductService) {}

  ngOnInit() {
    if (this.availableOnly) {
      this.searchProductService.getAvailableParams().subscribe((ps) => this.filterProductGroups(ps));
    }
  }

  private filterProductGroups(ps: AvailableParamsResource): void {
    this.productCategories = this.productCategories.map((c) => {
      return {...c, productGroups: c.productGroups.filter((g) => ps.productGroups.indexOf(g.value) >= 0)};
    });
  }

  writeValue(value: string): void {
    if (!value) {
      return;
    }
    /* change detection :-( */
    setTimeout(() => {
      this.getSelectedProductGroup().setValue(value, {emitEvent: false});
    }, 0);
  }

  registerOnChange(fn: any): void {
    this.getSelectedProductGroup().valueChanges.subscribe(fn);
  }

  registerOnTouched() {
    /* needed for control value accessor */
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.productGroupForm.disable();
    } else {
      this.productGroupForm.enable();
    }
  }

  private getSelectedProductGroup(): AbstractControl {
    return this.productGroupForm.controls.selectedProductGroup;
  }
}
